/**
 * Pure whiteboard logic: strokes and notes live in board units (u right, v down, 0..1) so
 * every client draws the same picture at its own resolution. Everything here runs under
 * Vitest; the WebGL and networking side is Whiteboard.ts (this file is board.ts because
 * Windows cannot tell the two names apart).
 */
import { WHITEBOARD, type WbNote, type WbStroke, type WhiteboardState } from '@clinspace/shared';

export type WbTool = 'pen' | 'eraser' | 'note';

/** Board aspect: the pixel canvas keeps it, so one unit of u is 3x one unit of v in metres. */
export const BOARD_ASPECT = WHITEBOARD.width / WHITEBOARD.height;

/** Sticky note size in board units (0.9 x 0.9 m on a 6 x 2 m board is a fair post-it). */
export const NOTE_SIZE = { u: 0.15, v: 0.45 };

/** Distance in board units (scaled for the aspect) between two points. */
export function boardDistance(u0: number, v0: number, u1: number, v1: number): number {
  const du = (u0 - u1) * BOARD_ASPECT;
  const dv = v0 - v1;
  return Math.hypot(du, dv);
}

/**
 * Adds a point to a stroke in progress unless it is too close to the last one (a resting
 * pointer must not pile up hundreds of points); returns true when added.
 */
export function appendPoint(points: number[], u: number, v: number, minStep = 0.0025): boolean {
  const n = points.length;
  if (n >= WHITEBOARD.pointLimit * 2) return false;
  if (n >= 2 && boardDistance(points[n - 2]!, points[n - 1]!, u, v) < minStep) return false;
  points.push(u, v);
  return true;
}

/** Distance from a point to a segment, in aspect-corrected board units. */
export function segmentDistance(
  u: number,
  v: number,
  u0: number,
  v0: number,
  u1: number,
  v1: number,
): number {
  const ax = u0 * BOARD_ASPECT;
  const bx = u1 * BOARD_ASPECT;
  const px = u * BOARD_ASPECT;
  const dx = bx - ax;
  const dy = v1 - v0;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (v - v0) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), v - (v0 + t * dy));
}

/** True when the eraser at (u, v) with the given radius touches the stroke. */
export function strokeHit(stroke: WbStroke, u: number, v: number, radius: number): boolean {
  const p = stroke.points;
  const reach = radius + stroke.width * BOARD_ASPECT;
  if (p.length === 2) return boardDistance(p[0]!, p[1]!, u, v) <= reach;
  for (let i = 0; i + 3 < p.length; i += 2)
    if (segmentDistance(u, v, p[i]!, p[i + 1]!, p[i + 2]!, p[i + 3]!) <= reach) return true;
  return false;
}

/** The note's rectangle in board units (anchored at its top-left, kept inside the board). */
export function noteRect(note: Pick<WbNote, 'u' | 'v'>): {
  u: number;
  v: number;
  w: number;
  h: number;
} {
  return {
    u: Math.min(note.u, 1 - NOTE_SIZE.u),
    v: Math.min(note.v, 1 - NOTE_SIZE.v),
    w: NOTE_SIZE.u,
    h: NOTE_SIZE.v,
  };
}

export function noteAt(notes: WbNote[], u: number, v: number): WbNote | undefined {
  // Later notes draw on top, so search from the end.
  for (let i = notes.length - 1; i >= 0; i--) {
    const r = noteRect(notes[i]!);
    if (u >= r.u && u <= r.u + r.w && v >= r.v && v <= r.v + r.h) return notes[i];
  }
  return undefined;
}

/** Ids of every stroke and note the eraser touches at (u, v). */
export function eraseAt(state: WhiteboardState, u: number, v: number, radius = 0.012): string[] {
  const ids: string[] = [];
  for (const s of state.strokes) if (strokeHit(s, u, v, radius)) ids.push(s.id);
  const note = noteAt(state.notes, u, v);
  if (note) ids.push(note.id);
  return ids;
}

/** Removes ids from a state copy (what the net layer does on wb:erase). */
export function applyErase(state: WhiteboardState, ids: string[]): WhiteboardState {
  const gone = new Set(ids);
  return {
    strokes: state.strokes.filter((s) => !gone.has(s.id)),
    notes: state.notes.filter((n) => !gone.has(n.id)),
  };
}

/** Readable label for a pen width. */
export function widthLabel(width: number): string {
  const i = (WHITEBOARD.widths as readonly number[]).indexOf(width);
  return i === 0 ? 'Fine' : i === 1 ? 'Medium' : 'Broad';
}

interface DrawHelpers {
  wrap: (ctx: CanvasRenderingContext2D, s: string, maxWidth: number) => string[];
  font: (size: number, weight?: number) => string;
}

/**
 * Draws the board into a 2D context of `pxW` x `pxH` logical pixels: paper, faint grid, every
 * stroke as a smoothed polyline, every note as a card with wrapped text and the author.
 */
export function drawBoard(
  ctx: CanvasRenderingContext2D,
  state: WhiteboardState,
  pxW: number,
  pxH: number,
  opts: DrawHelpers & {
    hoveredNote?: string;
    /** Stroke being drawn locally, drawn last. */
    live?: WbStroke;
  },
): void {
  ctx.fillStyle = '#fbfbf9';
  ctx.fillRect(0, 0, pxW, pxH);
  ctx.strokeStyle = 'rgba(34,52,77,0.07)';
  ctx.lineWidth = 1;
  const step = pxW / 24;
  ctx.beginPath();
  for (let x = step; x < pxW; x += step) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, pxH);
  }
  for (let y = step; y < pxH; y += step) {
    ctx.moveTo(0, y);
    ctx.lineTo(pxW, y);
  }
  ctx.stroke();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const strokes = opts.live ? [...state.strokes, opts.live] : state.strokes;
  for (const s of strokes) drawStroke(ctx, s, pxW, pxH);
  ctx.textBaseline = 'alphabetic';
  for (const n of state.notes) drawNote(ctx, n, pxW, pxH, n.id === opts.hoveredNote, opts);
}

export function drawStroke(
  ctx: CanvasRenderingContext2D,
  s: WbStroke,
  pxW: number,
  pxH: number,
): void {
  const p = s.points;
  if (p.length < 2) return;
  ctx.strokeStyle = s.colour;
  ctx.fillStyle = s.colour;
  ctx.lineWidth = Math.max(1.5, s.width * pxW);
  if (p.length === 2) {
    ctx.beginPath();
    ctx.arc(p[0]! * pxW, p[1]! * pxH, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(p[0]! * pxW, p[1]! * pxH);
  if (p.length === 4) ctx.lineTo(p[2]! * pxW, p[3]! * pxH);
  else {
    // Quadratic through the midpoints: a smooth line from a jittery ray.
    for (let i = 2; i + 3 < p.length; i += 2) {
      const mx = ((p[i]! + p[i + 2]!) / 2) * pxW;
      const my = ((p[i + 1]! + p[i + 3]!) / 2) * pxH;
      ctx.quadraticCurveTo(p[i]! * pxW, p[i + 1]! * pxH, mx, my);
    }
    ctx.lineTo(p[p.length - 2]! * pxW, p[p.length - 1]! * pxH);
  }
  ctx.stroke();
}

function drawNote(
  ctx: CanvasRenderingContext2D,
  n: WbNote,
  pxW: number,
  pxH: number,
  hovered: boolean,
  opts: DrawHelpers,
): void {
  const r = noteRect(n);
  const x = r.u * pxW;
  const y = r.v * pxH;
  const w = r.w * pxW;
  const h = r.h * pxH;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = n.colour;
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  if (hovered) {
    ctx.strokeStyle = '#22344d';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
  }
  const pad = w * 0.08;
  const size = Math.round(h * 0.11);
  ctx.font = opts.font(size, 600);
  ctx.fillStyle = '#17212f';
  ctx.textAlign = 'left';
  const lines = opts.wrap(ctx, n.text, w - pad * 2);
  const lineH = size * 1.45;
  const maxLines = Math.max(1, Math.floor((h - pad * 2 - lineH) / lineH));
  lines.slice(0, maxLines).forEach((line, i) => {
    ctx.fillText(line, x + pad, y + pad + lineH * (i + 0.8), w - pad * 2);
  });
  ctx.font = opts.font(Math.round(size * 0.8), 500);
  ctx.fillStyle = 'rgba(23,33,47,0.65)';
  ctx.fillText(n.by.name, x + pad, y + h - pad * 0.8, w - pad * 2);
}
